import React, { useState, useEffect } from 'react';
import { Card, List, Typography, Tag, Tooltip, Spin, message, Button, Collapse, Descriptions, Space, Divider } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined, HistoryOutlined, ArrowLeftOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ImportHistoryPage.css';

const { Text, Title } = Typography;
const { Panel } = Collapse;

const ImportHistoryPage = () => {
  const [importHistory, setImportHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchImportHistory();
  }, []);

  const fetchImportHistory = async () => {
    try {
      const response = await axios.get('/api/orders/import_history/');
      setImportHistory(response.data.import_history);
    } catch (error) {
      console.error('Error fetching import history:', error);
      message.error('获取导入历史失败');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />;
      case 'ERROR':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />;
      case 'PROCESSING':
        return <ClockCircleOutlined style={{ color: '#1890ff', fontSize: '16px' }} />;
      default:
        return <ClockCircleOutlined style={{ color: '#d9d9d9', fontSize: '16px' }} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'COMPLETED':
        return '已完成';
      case 'ERROR':
        return '失败';
      case 'PROCESSING':
        return '进行中';
      default:
        return '未知';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'ERROR':
        return 'error';
      case 'PROCESSING':
        return 'processing';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const renderImportDetails = (item) => {
    const hasErrors = item.error_count > 0;
    const hasDuplicates = item.duplicate_count > 0;
    
    return (
      <div className="import-details">
        <Descriptions column={2} size="small">
          <Descriptions.Item label="导入ID">{item.import_id}</Descriptions.Item>
          <Descriptions.Item label="导入时间">{formatDateTime(item.created_at)}</Descriptions.Item>
          <Descriptions.Item label="快递公司">{item.courier_service}</Descriptions.Item>
          <Descriptions.Item label="港口">{item.port}</Descriptions.Item>
          <Descriptions.Item label="总记录数">{item.total_records}</Descriptions.Item>
          <Descriptions.Item label="进度">{item.progress_percentage}%</Descriptions.Item>
          <Descriptions.Item label="成功导入">{item.successful_count}</Descriptions.Item>
          <Descriptions.Item label="导入失败">{item.error_count}</Descriptions.Item>
          {hasDuplicates && (
            <Descriptions.Item label="重复订单">{item.duplicate_count}</Descriptions.Item>
          )}
          {item.customer && (
            <>
              <Descriptions.Item label="客户ID">{item.customer.id}</Descriptions.Item>
              <Descriptions.Item label="客户名称">{item.customer.company_name}</Descriptions.Item>
            </>
          )}
        </Descriptions>

        {item.status === 'ERROR' && item.error_message && (
          <>
            <Divider style={{ margin: '16px 0' }} />
            <div className="error-section">
              <Text type="danger" strong>错误信息:</Text>
              <div className="error-message-detail">
                {item.error_message}
              </div>
            </div>
          </>
        )}

        {hasErrors && item.error_orders && item.error_orders.length > 0 && (
          <>
            <Divider style={{ margin: '16px 0' }} />
            <div className="error-orders-section">
              <Text type="danger" strong>Error orders:</Text>
              <div className="error-orders-list">
                {item.error_orders.map((errorOrder, index) => (
                  <div key={index} className="error-order-item">
                    <Text strong>订单号: {errorOrder.order_number}</Text>
                    <Text type="danger" className="error-order-message">
                      {errorOrder.error}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderImportItem = (item, index) => {
    const hasErrors = item.error_count > 0;
    const hasDuplicates = item.duplicate_count > 0;
    
    return (
      <Card className="import-history-item-card" key={item.import_id}>
        <Collapse 
          ghost
          expandIconPosition="end"
          className="import-collapse"
        >
          <Panel
            header={
              <div className="import-item-header">
                <div className="import-status-section">
                  {getStatusIcon(item.status)}
                  <Tag color={getStatusColor(item.status)} className="status-tag">
                    {getStatusText(item.status)}
                  </Tag>
                  <Text className="import-time" type="secondary">
                    {formatDateTime(item.created_at)}
                  </Text>
                </div>
                
                <div className="import-summary-section">
                  <Space size="middle">
                    <div className="summary-stat">
                      <Text strong style={{ color: '#52c41a' }}>{item.successful_count}</Text>
                      <Text type="secondary"> 成功</Text>
                    </div>
                    {hasDuplicates && (
                      <div className="summary-stat">
                        <Text strong style={{ color: '#faad14' }}>{item.duplicate_count}</Text>
                        <Text type="secondary"> 重复</Text>
                      </div>
                    )}
                    {hasErrors && (
                      <div className="summary-stat">
                        <Text strong style={{ color: '#ff4d4f' }}>{item.error_count}</Text>
                        <Text type="secondary"> 失败</Text>
                      </div>
                    )}
                  </Space>
                </div>
                
                <div className="import-meta-section">
                  <Text type="secondary" className="import-service">
                    {item.courier_service} · {item.port}
                  </Text>
                  {item.customer && (
                    <Text type="secondary" className="import-customer">
                      · {item.customer.company_name}
                    </Text>
                  )}
                </div>
              </div>
            }
            key={item.import_id}
          >
            {renderImportDetails(item)}
          </Panel>
        </Collapse>
      </Card>
    );
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="import-history-page">
        <div className="page-header">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleGoBack}
            style={{ marginRight: 16 }}
          >
            返回
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            <HistoryOutlined style={{ marginRight: 8 }} />
            导入历史
          </Title>
        </div>
        <div className="loading-container">
          <Spin size="large" />
          <Text type="secondary" style={{ marginTop: 16, display: 'block' }}>
            正在加载导入历史...
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="import-history-page">
      <div className="page-header">
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleGoBack}
          style={{ marginRight: 16 }}
        >
          返回
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          <HistoryOutlined style={{ marginRight: 8 }} />
          导入历史
        </Title>
        <Text type="secondary" style={{ marginLeft: 16 }}>
          最近 {importHistory.length} 次导入记录
        </Text>
      </div>

      <div className="import-history-content">
        {importHistory.length === 0 ? (
          <Card className="empty-state">
            <div className="empty-content">
              <InfoCircleOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
              <Title level={4} type="secondary">暂无导入记录</Title>
              <Text type="secondary">您还没有进行过任何订单导入操作</Text>
            </div>
          </Card>
        ) : (
          <div className="import-history-list">
            {importHistory.map((item, index) => renderImportItem(item, index))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportHistoryPage;
